'use client'

import { useState, useEffect, RefObject, useCallback } from 'react'

interface UseInViewportOptions {
  /** Margin around the root element (like CSS margin) */
  rootMargin?: string
  /** Threshold(s) at which to trigger callback */
  threshold?: number | number[]
  /** Once visible, stop observing (for lazy loading) */
  triggerOnce?: boolean
  /** Skip observation when disabled */
  enabled?: boolean
}

interface UseInViewportResult {
  /** Whether the element is currently in viewport */
  isInViewport: boolean
  /** Whether the element has been in viewport at least once */
  hasBeenInViewport: boolean
  /** Ref callback to attach to the target element */
  setRef: (node: HTMLElement | null) => void
}

/**
 * Custom hook to detect when an element enters the viewport.
 * Useful for lazy loading content and reducing initial API calls.
 */
export function useInViewport(
  options: UseInViewportOptions = {}
): UseInViewportResult {
  const {
    rootMargin = '100px 0px', // Pre-load 100px before entering viewport
    threshold = 0,
    triggerOnce = true,
    enabled = true,
  } = options

  const [isInViewport, setIsInViewport] = useState(false)
  const [hasBeenInViewport, setHasBeenInViewport] = useState(false)
  const [node, setNode] = useState<HTMLElement | null>(null)

  const setRef = useCallback((newNode: HTMLElement | null) => {
    setNode(newNode)
  }, [])

  useEffect(() => {
    if (!node || !enabled) return

    // If triggerOnce and already been visible, don't observe
    if (triggerOnce && hasBeenInViewport) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        const isVisible = entry.isIntersecting

        setIsInViewport(isVisible)

        if (isVisible && !hasBeenInViewport) {
          setHasBeenInViewport(true)

          // Stop observing if triggerOnce
          if (triggerOnce) {
            observer.disconnect()
          }
        }
      },
      { rootMargin, threshold }
    )

    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [node, rootMargin, threshold, triggerOnce, enabled, hasBeenInViewport])

  return { isInViewport, hasBeenInViewport, setRef }
}

export default useInViewport
